import type { PresentsCheckResBody } from '@/@types/api-v1/present'
import { api } from '@/components/Functional/lib/request'
import { logger } from '@/components/Functional/utils/logger'
import {
  UserActivitiesResponseSchema,
  UserGetResponseSchema,
  UserRewardStatusGetResponseSchema,
  UserRewardStatusListResponseSchema,
  UserRewardTransactionGetResponseSchema,
  UserSheetListResponseSchema,
  UserStampGetResponseSchema,
  UserStampListResponseSchema,
} from '@/schemas/v2'
import type {
  UserActivitiesResponse,
  UserGetResponse,
  UserRewardStatusGetResponse,
  UserRewardStatusListResponse,
  UserRewardTransactionGetResponse,
  UserSheetListResponse,
  UserStampGetResponse,
  UserStampListResponse,
} from '@/schemas/v2'
import {
  type MynaviRewardTransactionHistoryResponse,
  MynaviRewardTransactionHistoryResponseSchema,
} from '@/schemas/v2/mynavi'
import type { ApplicationFormSubmitRequest } from '@/schemas/v2/reward'

const BASE = process.env.NEXT_PUBLIC_API_URL ?? ''

type FetchOptions = { headers?: Record<string, string> }

export async function fetchUserProfile(options?: FetchOptions): Promise<UserGetResponse> {
  const url = `${BASE}/api/v2/user`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postUserRecovery(recoveryCode: string, options?: FetchOptions): Promise<void> {
  const url = `${BASE}/api/v2/user/recovery`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    await api.post<void>(url, { recoveryCode }, options)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchUserSheets(options?: FetchOptions): Promise<UserSheetListResponse> {
  const url = `${BASE}/api/v2/user/sheets`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserSheetListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchUserRewards(options?: FetchOptions): Promise<UserRewardStatusListResponse> {
  const url = `${BASE}/api/v2/user/rewards`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserRewardStatusListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchUserReward(id: number, options?: FetchOptions): Promise<UserRewardStatusGetResponse> {
  const url = `${BASE}/api/v2/user/rewards/${id}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserRewardStatusGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postRewardApplicationForm(
  transactionId: number,
  body: ApplicationFormSubmitRequest,
  options?: FetchOptions,
): Promise<void> {
  const url = `${BASE}/api/v2/user/reward-transactions/${transactionId}/application-form`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    await api.post<void>(url, body, options)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchUserStamps(options?: FetchOptions): Promise<UserStampListResponse> {
  const url = `${BASE}/api/v2/user/stamps`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserStampListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchUserStamp(stampId: number, options?: FetchOptions): Promise<UserStampGetResponse> {
  const url = `${BASE}/api/v2/user/stamps/${stampId}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserStampGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postUserRewardTransaction(
  transactionId: number,
  body?: Record<string, unknown>,
  options?: FetchOptions,
): Promise<UserRewardTransactionGetResponse> {
  const url = `${BASE}/api/v2/user/reward-transactions/${transactionId}/exchange`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body ?? {}, options)
    return UserRewardTransactionGetResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function postUserReset(options?: FetchOptions): Promise<void> {
  const url = `${BASE}/api/v2/user/reset`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    await api.post<void>(url, {}, options)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchPresentCount(options?: FetchOptions): Promise<PresentsCheckResBody> {
  const url = `${BASE}/api/v1/presents/check/`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    // Zod スキーマが未整備のため型アサーションを使用（v2 スキーマ整備は別 Issue で対応）
    return data as PresentsCheckResBody
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

export async function fetchUserActivities(
  cursor?: string | null,
  options?: FetchOptions,
): Promise<UserActivitiesResponse> {
  const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
  const url = `${BASE}/api/v2/user/activities${params}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return UserActivitiesResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

// ─── GET /api/v2/user/reward-transactions（認証必須）─────────────────────

export async function fetchUserRewardTransactions(
  cursor?: string | null,
  options?: FetchOptions,
): Promise<MynaviRewardTransactionHistoryResponse> {
  const params = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
  const url = `${BASE}/api/v2/user/reward-transactions${params}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return MynaviRewardTransactionHistoryResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}
